import React, { useCallback, useState } from 'react'
import {
  Card,
  IconButton,
  DataBlock,
  Row,
  colors,
  BillboardButton,
  Paragraph,
  Separator,
  usePageWidth,
} from '@fs/zion-ui'
import { ContentEdit, TreePedigree, ContentCheck } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  getNestedSubelementsByType,
  getPersonName,
  getSexTranslatedOrOrig,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext } from '../../providers/FamilyReviewContext'
import { getRelationshipText, getBackgroundColor } from '../../utils/utils'
import { showEssentialInfoHighlights } from '../../utils/highlightUtils'

export default function RelationshipReviewCard({ personId, currentTask, editPersonOverlay, handleCorrectClick }) {
  const h = useSlsHighlightsContext()
  const zionSlsState = useSlsStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const state = useFamilyReviewStateContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })

  const hasBeenReviewed = state.stepTwo.relationshipCards.find(
    (cardState) => cardState.personId === personId
  ).hasBeenReviewed

  const isFocused = state.selectedPersonId === personId
  const [isHovered, setIsHovered] = useState(false)

  const openEditPanel = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_PERSON_ID', value: personId })
    showEssentialInfoHighlights({
      h,
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      personIds: [personId],
      isSmallScreen,
    })
    editPersonOverlay.open()
  }, [h, currentTask, dispatch, personId, editPersonOverlay, isSmallScreen, state.splitScreenPartitionKey])

  const handleMouseDown = () => {
    setIsHovered(true)
    showEssentialInfoHighlights({
      h,
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      personIds: [personId],
      isSmallScreen,
    })
  }

  const personElement = currentTask.subElements.find((el) => el.id === personId)
  const personName = getPersonName({ person: personElement, justOneName: true })
  const personSexField = getNestedSubelementsByType(personElement, 'SEX_CODE')?.[0]
  const translatedPersonSex = getSexTranslatedOrOrig(personSexField, t)
  const backgroundColor = getBackgroundColor(hasBeenReviewed, isFocused, colors.gray10, colors.yellow10, colors.green10)
  const cardBackgroundColor = getBackgroundColor(
    hasBeenReviewed,
    isFocused,
    colors.white100,
    colors.yellow02,
    colors.white100
  )

  return (
    <Card
      edges={isFocused || isHovered ? 'elevation' : 'border'}
      data-hasbeenreviewed={hasBeenReviewed}
      backgroundColor={cardBackgroundColor}
      onMouseDown={handleMouseDown}
    >
      <Row>
        <TreePedigree size="md" background={backgroundColor} noShrinkX />
        <DataBlock
          growX
          heading={getRelationshipText(personElement, zionSlsState.types, currentTask, t)}
          data={
            <Paragraph size="sm">
              {t('edit-essential-info.title', 'Name')}: {personName}
              <br />
              {t('edit-essential-info.sex-code', 'Sex')}: {translatedPersonSex}
            </Paragraph>
          }
        />
        {atPageWidth({
          default: (
            <>
              {!hasBeenReviewed && (
                <>
                  <IconButton
                    size="sm"
                    emphasis="high"
                    disabled={!isFocused}
                    Icon={ContentCheck}
                    data-testid="Correct Person Icon"
                    description={t('family-review.step2.correct-button', 'Correct')}
                    linkName="Review Relationship Correct"
                    onClick={() => handleCorrectClick(personId)}
                  />
                  <Separator size="xs" />
                </>
              )}
              <IconButton
                size="sm"
                emphasis="medium"
                disabled={!hasBeenReviewed && !isFocused}
                Icon={ContentEdit}
                data-testid="Edit Person Icon"
                description={t('family-review.step2.edit-button', 'Edit')}
                linkName="Review Relationship Edit"
                onClick={openEditPanel}
              />
            </>
          ),
          sm: hasBeenReviewed && (
            <IconButton
              size="sm"
              noShrinkX
              emphasis="medium"
              Icon={ContentEdit}
              data-testid="Edit Person Icon"
              description={t('family-review.step2.edit-button', 'Edit')}
              linkName="Review Person Edit"
              onClick={openEditPanel}
            />
          ),
        })}
      </Row>
      {!hasBeenReviewed &&
        atPageWidth({
          default: null,
          sm: (
            <>
              <Separator />
              <Row alignX="center" wrap guttersY="xxs">
                <BillboardButton
                  noShrinkX
                  onClick={openEditPanel}
                  color={isFocused ? 'yellow' : 'blue'}
                  emphasis={isFocused ? 'medium' : 'low'}
                  linkName="Review Relationship Edit"
                  data-testid="Edit"
                  Icon={ContentEdit}
                >
                  {t('family-review.step2.edit-button', 'Edit')}
                </BillboardButton>
                <BillboardButton
                  noShrinkX
                  onClick={() => handleCorrectClick(personId)}
                  color={isFocused ? 'yellow' : 'blue'}
                  emphasis={isFocused ? 'lightHigh' : 'medium'}
                  linkName="Review Relationship Correct"
                  data-testid="Correct"
                  Icon={ContentCheck}
                >
                  {t('family-review.step2.correct-button', 'Correct')}
                </BillboardButton>
              </Row>
            </>
          ),
        })}
    </Card>
  )
}
